import {FC, useMemo, useState} from 'react';

import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import {When} from 'react-if';

import Button from 'components/button/Button';
import {useAppSelector, useAppDispatch} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useBasketCalculations} from 'hooks/useBasketCalculations';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {getBasketCalculationAction, setSelectedItemsAction} from 'store/actions/basket-action';
import {
  ShippingInfo,
  MarketplaceShopType,
  ProductSectionTypes,
  BasketItemType,
  BasketLineItem,
  ExpressTimeFrame
} from 'types';
import {
  checkIfAnyItemFromSectionIsSelected,
  getAlcoholVolumeInLiters,
  getIdsFromBasketItems,
  isAllItemsFromThisSectionAreSelected,
  isTimeEligibleForExpress
} from 'utils';
import {isSyncBasketEnabled, safeToFixed} from 'utils/helper';
import {BasketFreeDeliveryBanner} from 'views/basket-banners';
import {BasketShippingFeeDiscountBanner} from 'views/basket-banners/components/BasketShippingFeeDiscountBanner';
import {BasketShippingFeePromotionBanner} from 'views/basket-banners/components/BasketShippingFeePromotionBanner';
import {BasketDeliveryDetails1PModal, BasketDeliveryDetails3PModal} from 'views/basket-modals';
import {ExpressDeliveryDetailsModal} from 'views/basket-modals/ExpressDeliveryDetailsModal';
import {ExpressOutOfWorkingHoursModal} from 'views/basket-modals/ExpressOutOfWorkingHoursModal';

import {
  BasketSellerHeader1P,
  BasketSellerHeader3P,
  BasketSellerHeaderAlco,
  BasketSellerHeaderBulky,
  BasketSellerHeaderMakroFleet,
  BasketSellerHeaderPreOrder
} from './basketSellerHeader.component';
import {
  StyledAvailableProducts,
  StyledSectionHeader,
  StyledUnavailableAlcoProductsLessThenAllowed,
  StyledUnavailableProducts
} from './styles';
import {ALCOHOL_VOLUME_THRESHOLD} from '../../constants/index';
import {BasketDeliveryDetailsBulkyModalV2} from '../basket-modals/BasketDeliveryDetailsBulkyModalV2';
import {BasketDeliveryDetailsMakroFleetModal} from '../basket-modals/BasketDeliveryDetailsMakroFleetModal';
import {DeliveryDetailsPreOrderModal} from '../basket-modals/DeliveryDetailsPreOrderModal';

export type SectionHeaderProps = {
  title: string;
  description: string;
  index: number;
  header?: string;
  marketplaceType?: string;
  type?: ProductSectionTypes;
  onlyBulkyItems?: boolean;
  productId?: string;
  onlyPreOrderItems?: boolean;
  onlyAlcoItems?: boolean;
  only1PxBulkyItems?: boolean;
  onlyBulkyAnd1PxBulkyItems?: boolean;
  shippingInfo?: ShippingInfo;
  data: BasketLineItem[];
  deleteUnavailableItems: () => void;
};

export type SectionHeaderV2Props = SectionHeaderProps & {
  shopId: number;
  isFlexibleBasketEnabled: boolean;
  shippingFeePromotionText?: string;
};
export const SectionHeaderV2: FC<SectionHeaderV2Props> = ({
  title,
  description,
  type,
  index,
  header,
  marketplaceType,
  shopId,
  onlyBulkyItems,
  onlyPreOrderItems,
  onlyAlcoItems,
  only1PxBulkyItems,
  onlyBulkyAnd1PxBulkyItems,
  shippingInfo,
  isFlexibleBasketEnabled,
  data,
  deleteUnavailableItems,
  shippingFeePromotionText
}) => {
  const dispatch = useAppDispatch();
  const {isExpress} = useAppMode();
  const {basket} = useAppSelector((state) => state?.basket);
  const {defaultShippingFee, priceUnit} = useBasketCalculations();
  const [open1PModal, setOpen1PModal] = useState(false);
  const [open3PModal, setOpen3PModal] = useState(false);
  const [openBulkyModal, setOpenBulkyModal] = useState(false);
  const [openPreOrderModal, setOpenPreOrderModal] = useState(false);
  const [openMakroFleetModal, setOpenMakroFleetModal] = useState(false);
  const [openExpressModal, setOpenExpressModal] = useState(false);
  const [openExpressOutOfTimeModal, setOpenExpressOutOfTimeModal] = useState(false);

  const isUnAvailableSection = type === ProductSectionTypes.OUT_OF_STOCK_PRODUCTS;
  const isAvailableSection = type === ProductSectionTypes.AVAILABLE_PRODUCTS;
  const isExpressSection = type === ProductSectionTypes.EXPRESS_PRODUCTS;
  const isAlcoholProhibitedPeriod = type === ProductSectionTypes.ALCOHOL_PROHIBITED_PERIOD;
  const isAlcoholLessThanRequired = type === ProductSectionTypes.ALCOHOL_LESS_THAN_REQUIRED;
  const isAlcoholLicenseExpired = type === ProductSectionTypes.ALCOHOL_EXPIRED_LICENSE;
  const isBulky = marketplaceType === ProductSectionTypes.BULKY_PRODUCTS;
  const isPreOrder = marketplaceType === ProductSectionTypes.PRE_ORDER_PRODUCTS;
  const is1PSeller = marketplaceType === MarketplaceShopType.CONSIGNMENT;
  const isAlco = marketplaceType === ProductSectionTypes.ALCOHOL_PRODUCTS;
  const isMakroFleet = marketplaceType === BasketItemType.MAKRO_FLEET;
  const is1Px = marketplaceType === BasketItemType.ONEPX;
  const isReactNativeWebView = ReactNative.isReactNativeWebView();
  const isSyncBasket = isSyncBasketEnabled();

  const storeSelecteditems = useAppSelector((state) => state?.basket?.selectedItems);
  const localeSelectedItems = JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
  const selectedItems = isSyncBasket ? storeSelecteditems : localeSelectedItems;
  const noItemSelected = !selectedItems?.length;

  const isAnyItemFromTheSectionSelected = useMemo(
    () => checkIfAnyItemFromSectionIsSelected(data, selectedItems),
    [data, selectedItems]
  );

  const is3PSeller = !is1PSeller;

  if (!basket?.prices) {
    return null;
  }

  const {
    subtotal = 0,
    shippingFee,
    originalShippingFee,
    is3PSeller: is3PSellerShop
  } = basket?.prices.priceBreakdown.find((item) => item.shopId === shopId) || {};

  const hasShippingFeePromotionApplied = shippingFee && originalShippingFee && originalShippingFee > shippingFee;
  const shippingFeePromotion = (originalShippingFee || 0) - (shippingFee || 0);

  const getRemainingAmountOfAlcohol = () => {
    const remaining = ALCOHOL_VOLUME_THRESHOLD - getAlcoholVolumeInLiters(basket.unavailableItems) || 0;
    return remaining >= 0.01 ? remaining : 0.01;
  };

  const toggle1PDeliveryModal = () => setOpen1PModal(!open1PModal);

  const toggle3PDeliveryModal = () => setOpen3PModal(!open3PModal);

  const toggleMakroFleetDeliveryModal = () => setOpenMakroFleetModal((flag) => !flag);

  const toggleExpressModal = () => {
    const isDuringExpressHours = isTimeEligibleForExpress(
      basket.storeInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame
    );
    isDuringExpressHours ? setOpenExpressModal((prev) => !prev) : setOpenExpressOutOfTimeModal((prev) => !prev);
  };

  const openBulkyDeliveryModal = () => setOpenBulkyModal(true);
  const closeBulkyDeliveryModal = () => setOpenBulkyModal(false);

  const openPreOrderDeliveryModal = () => setOpenPreOrderModal(true);
  const closePreOrderDeliveryModal = () => {
    setOpenPreOrderModal(false);
  };

  const toggleSellerDeliveryModal = () => {
    if (onlyPreOrderItems) {
      openPreOrderDeliveryModal();
    } else if (onlyBulkyItems || onlyBulkyAnd1PxBulkyItems || only1PxBulkyItems) {
      openBulkyDeliveryModal();
    } else {
      toggle1PDeliveryModal();
    }
  };

  const onSelectSection = (e: any) => {
    const sectionItemsIds = getIdsFromBasketItems(data);
    const filteredIds = sectionItemsIds.filter((id) => !selectedItems.includes(id));

    if (e?.target?.checked) {
      dispatch(setSelectedItemsAction([...selectedItems, ...filteredIds]));
      !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify([...selectedItems, ...filteredIds]));
    } else {
      const filteredItems = selectedItems.filter((item: string) => !sectionItemsIds.includes(item));
      dispatch(setSelectedItemsAction(filteredItems));
      !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify(filteredItems));
    }
    dispatch(getBasketCalculationAction());
  };

  const isSelectedAllBasketItems = basket.basketItems.length === selectedItems.length;
  const isAllItemsFromThisSectionSelected = isAllItemsFromThisSectionAreSelected(data, selectedItems);

  return (
    <>
      <StyledSectionHeader
        className={`v2 ${
          isUnAvailableSection || isAlcoholProhibitedPeriod || isAlcoholLessThanRequired || isAlcoholLicenseExpired
            ? 'unavailable'
            : ''
        }`}
        isFlexibleBasketEnabled={isFlexibleBasketEnabled}
        key={index}
      >
        <When condition={header?.length}>
          <When condition={isUnAvailableSection || isAlcoholProhibitedPeriod || isAlcoholLicenseExpired}>
            <StyledUnavailableProducts isFlexibleBasketEnabled={isFlexibleBasketEnabled}>
              <Box paddingLeft={isFlexibleBasketEnabled ? '12px' : '0px'}>
                <Typography component={'p'} color="primary.dark" fontWeight={700}>
                  {header}
                </Typography>
              </Box>
              <When condition={isFlexibleBasketEnabled}>
                <Box>
                  <Button onClick={deleteUnavailableItems}>{t('flexiBasket.deleteAll')}</Button>
                </Box>
              </When>
            </StyledUnavailableProducts>
          </When>

          <When condition={isAvailableSection}>
            <StyledAvailableProducts>
              <Typography component={'p'} color="success.dark" fontWeight={700}>
                {header}
              </Typography>
            </StyledAvailableProducts>
          </When>
        </When>

        <When condition={isAlcoholLessThanRequired}>
          <BasketSellerHeaderAlco
            isUnavailable={true}
            title={t('alcohol.title.alcoProducts')}
            message={t('alcohol.label.readT&C')}
            onClick={() => SendBrowserEvent.onAlcoTermsAndConditionsPress()}
            minimumQuantityError={t('alcohol.message.minimumPurchase')}
            isFlexibleBasketEnabled={isFlexibleBasketEnabled}
            onSelectSection={onSelectSection}
            isSelectedAllBasketItems={isSelectedAllBasketItems}
            isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
            onClickDeliveryInfo={toggleSellerDeliveryModal}
            isReactNativeView={isReactNativeWebView}
          />
          <StyledUnavailableAlcoProductsLessThenAllowed>
            <Typography component={'p'} color="primary.dark" fontWeight={400}>
              <Trans
                i18nKey={isFlexibleBasketEnabled ? 'alcohol.message.addMoreAlcohol2' : 'alcohol.message.addMoreAlcohol'}
                values={{volume: +safeToFixed(getRemainingAmountOfAlcohol())}}
              />
            </Typography>
          </StyledUnavailableAlcoProductsLessThenAllowed>
        </When>

        <When condition={!!title}>
          <When
            condition={
              (!isFlexibleBasketEnabled && (is1PSeller || (onlyBulkyAnd1PxBulkyItems && isBulky))) ||
              (is1PSeller &&
                isFlexibleBasketEnabled &&
                !isBulky &&
                !isPreOrder &&
                !isAlco &&
                !isMakroFleet &&
                !is3PSeller &&
                !onlyPreOrderItems &&
                !onlyBulkyItems &&
                !onlyBulkyAnd1PxBulkyItems &&
                !only1PxBulkyItems)
            }
          >
            <BasketSellerHeader1P
              priceUnit={priceUnit}
              onClick={isExpress ? toggleExpressModal : toggleSellerDeliveryModal}
              subtotal={subtotal}
              currentShippingFee={shippingFee as number}
              defaultShippingFee={defaultShippingFee}
              deliveryBy={description}
              onlyBulkyItems={!!onlyBulkyItems}
              onlyPreOrderItems={!!onlyPreOrderItems}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              description={description}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              isReactNativeView={isReactNativeWebView}
            />
          </When>
          <When condition={is3PSeller && !is1Px && !isBulky && !isPreOrder && !isAlco && !isMakroFleet}>
            <BasketSellerHeader3P
              priceUnit={priceUnit}
              sellerName={title}
              deliveryBy={description}
              onClick={toggle3PDeliveryModal}
              subtotal={subtotal}
              shippingFee={shippingFee as number}
              originalShippingFee={originalShippingFee}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              isReactNativeView={isReactNativeWebView}
              showDeliveryFee={isAnyItemFromTheSectionSelected}
              shopLogo={data[0].productDetails.sellerDetails.shopLogo}
            />
          </When>
          <When condition={isMakroFleet}>
            <BasketSellerHeaderMakroFleet
              priceUnit={priceUnit}
              sellerName={title}
              description={t('label.chooseDeliverySlot')}
              onClick={toggleMakroFleetDeliveryModal}
              subtotal={subtotal}
              shippingFee={shippingFee as number}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              isReactNativeView={isReactNativeWebView}
            />
          </When>
          <When
            condition={(isBulky || onlyBulkyItems || is1Px || only1PxBulkyItems) && !isPreOrder && isAvailableSection}
          >
            <BasketSellerHeaderBulky
              onClick={openBulkyDeliveryModal}
              deliveryBy={description}
              title={title}
              onlyBulkyItems={!!onlyBulkyItems}
              only1PxBulkyItems={!!only1PxBulkyItems}
              is1PSeller={is1PSeller || (onlyBulkyAnd1PxBulkyItems && isBulky)}
              is1PX={is1Px}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              isReactNativeView={isReactNativeWebView}
            />
          </When>
          <When condition={(isPreOrder || onlyPreOrderItems) && isAvailableSection}>
            <BasketSellerHeaderPreOrder
              onClick={openPreOrderDeliveryModal}
              title={title}
              deliveryBy={description}
              onlyPreOrderItems={!!onlyPreOrderItems}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              isReactNativeView={isReactNativeWebView}
            />
          </When>
          <When condition={isAlco}>
            <When condition={onlyAlcoItems}>
              <BasketSellerHeader1P
                priceUnit={priceUnit}
                onClick={toggleSellerDeliveryModal}
                subtotal={subtotal}
                currentShippingFee={shippingFee as number}
                defaultShippingFee={defaultShippingFee}
                deliveryBy={description}
                onlyBulkyItems={!!onlyBulkyItems}
                onlyPreOrderItems={!!onlyPreOrderItems}
                isFlexibleBasketEnabled={isFlexibleBasketEnabled}
                onSelectSection={onSelectSection}
                isSelectedAllBasketItems={isSelectedAllBasketItems}
                isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
                isReactNativeView={isReactNativeWebView}
              />
            </When>
            <BasketSellerHeaderAlco
              title={title}
              message={description}
              isFlexibleBasketEnabled={isFlexibleBasketEnabled}
              onSelectSection={onSelectSection}
              onClick={() => SendBrowserEvent.onAlcoTermsAndConditionsPress()}
              isSelectedAllBasketItems={isSelectedAllBasketItems}
              isAllItemsFromThisSectionSelected={isAllItemsFromThisSectionSelected}
              onClickDeliveryInfo={toggleSellerDeliveryModal}
              isReactNativeView={isReactNativeWebView}
            />
          </When>
        </When>
      </StyledSectionHeader>

      <When condition={hasShippingFeePromotionApplied}>
        <BasketShippingFeeDiscountBanner discountAmount={shippingFeePromotion} />
      </When>

      <When
        condition={
          ((is1PSeller || onlyBulkyAnd1PxBulkyItems) && (isAvailableSection || isExpressSection)) || is3PSellerShop
        }
      >
        <BasketFreeDeliveryBanner shippingFee={shippingFee} noItemSelected={noItemSelected} is3P={is3PSellerShop} />
      </When>

      <When condition={!!shippingFeePromotionText}>
        <BasketShippingFeePromotionBanner message={shippingFeePromotionText as string} />
      </When>

      <BasketDeliveryDetails1PModal
        open={open1PModal}
        onClose={toggle1PDeliveryModal}
        isReactNativeWebView={isReactNativeWebView}
      />
      <BasketDeliveryDetails3PModal
        open={open3PModal}
        onClose={toggle3PDeliveryModal}
        sellerName={title}
        deliveryBy={description}
        isReactNativeWebView={isReactNativeWebView}
      />

      <BasketDeliveryDetailsBulkyModalV2
        open={openBulkyModal}
        onClose={closeBulkyDeliveryModal}
        is1Px={is1Px || only1PxBulkyItems}
        shippingInfo={shippingInfo}
        isReactNativeWebView={isReactNativeWebView}
      />

      <BasketDeliveryDetailsMakroFleetModal
        open={openMakroFleetModal}
        deliveryBy={description}
        sellerName={title}
        onClose={toggleMakroFleetDeliveryModal}
        isReactNativeWebView={isReactNativeWebView}
      />
      <DeliveryDetailsPreOrderModal
        open={openPreOrderModal}
        deliveryBy={description}
        onClose={closePreOrderDeliveryModal}
        isReactNativeWebView={isReactNativeWebView}
      ></DeliveryDetailsPreOrderModal>
      {isExpress && (
        <>
          <ExpressDeliveryDetailsModal
            open={openExpressModal}
            onClose={toggleExpressModal}
            isReactNativeWebView={isReactNativeWebView}
          />
          <ExpressOutOfWorkingHoursModal open={openExpressOutOfTimeModal} onClose={toggleExpressModal} />
        </>
      )}
    </>
  );
};

SectionHeaderV2.displayName = 'BasketDrawerSectionHeaderV2';
